/* MUSE & MAKER - mm-piece-fit.js, 28 Sep 2026. MAKE EVERY RENDER FIT ITS FRAME.
   Loaded by sections/mm-piece-index.liquid before mm-piece-sheet.js.
   THE PROBLEM (Clara, 28 Sep): the white renders do not share a margin. A
   sofa sits in a wide pale field, a cushion fills its file edge to edge, so
   one blanket CSS scale (1.28 on the cards, 2 on the sheet thumbs) cropped
   the cushions while the sofas still floated. THE FIX: measure each picture
   once in the browser - draw it small on a canvas, find the box of pixels
   that are not the background (transparent, or the colour of the corners) -
   and set a transform per image so that box fills the frame. Results are set
   as CSS variables on the <img> (--mmfit-s, --mmfit-tx, --mmfit-ty,
   --mmfit-ox, --mmfit-oy); the stylesheet reads them, with the old blanket
   scale as the fallback, so if a picture cannot be read (no CORS header, a
   broken file) nothing gets worse than before.
   TWO KINDS OF PICTURE: a RENDER (subject on a plain field) is fitted so its
   subject fills ~90% of the width or ~86% of the height, whichever binds; a
   PHOTOGRAPH (content reaches nearly every edge) is made to COVER the frame.
   ONE SCALE PER FAMILY: the sizes of one piece must stay comparable, so the
   size renders of a card (desktop hover swap) and the thumbs in the phone
   sheet take the SMALLEST scale of their family, each still centred on its
   own subject. The card front is editorial and fitted on its own.
   Shopify's CDN sends Access-Control-Allow-Origin: *, which is what lets the
   canvas be read. Measured at 240px wide - plenty for a bounding box. */
(function(){
  var cache={};
  function measure(url){
    if(!url)return Promise.resolve(null);
    if(cache[url])return cache[url];
    cache[url]=new Promise(function(res){
      var im=new Image();im.crossOrigin='anonymous';
      im.onload=function(){
        try{
          var W=240,H=Math.max(1,Math.round(W*im.naturalHeight/im.naturalWidth));
          var c=document.createElement('canvas');c.width=W;c.height=H;
          var x=c.getContext('2d',{willReadFrequently:true});x.drawImage(im,0,0,W,H);
          var d=x.getImageData(0,0,W,H).data;
          function px(i){return [d[i],d[i+1],d[i+2],d[i+3]];}
          var corners=[px(0),px((W-1)*4),px((H-1)*W*4),px(((H-1)*W+W-1)*4)];
          var bg=[0,0,0,0];corners.forEach(function(p){for(var k=0;k<4;k++)bg[k]+=p[k]/4;});
          var transparent=bg[3]<40;
          var x0=W,y0=H,x1=-1,y1=-1;
          for(var y=0;y<H;y++)for(var xx=0;xx<W;xx++){
            var i=(y*W+xx)*4,on;
            if(transparent)on=d[i+3]>24;
            else on=d[i+3]>24&&(Math.abs(d[i]-bg[0])+Math.abs(d[i+1]-bg[1])+Math.abs(d[i+2]-bg[2]))>30;
            if(on){if(xx<x0)x0=xx;if(xx>x1)x1=xx;if(y<y0)y0=y;if(y>y1)y1=y;}
          }
          if(x1<0){res(null);return;}
          res({x:x0/W,y:y0/H,w:(x1-x0+1)/W,h:(y1-y0+1)/H,nw:im.naturalWidth,nh:im.naturalHeight});
        }catch(e){res(null);}
      };
      im.onerror=function(){res(null);};
      im.src=url;
    });
    return cache[url];
  }
  /* Geometry for one <img> that fills its frame with object-fit:contain. Returns the scale that
     fits the subject, plus where the subject sits, without applying anything. */
  function geom(box,W,H,fillW,fillH){
    var r=Math.min(W/box.nw,H/box.nh),iw=box.nw*r,ih=box.nh*r,offX=(W-iw)/2,offY=(H-ih)/2;
    var cx=offX+box.x*iw,cy=offY+box.y*ih,cw=box.w*iw,ch=box.h*ih;
    var photo=(box.w*box.h)>0.85;
    var s=photo?Math.max(W/iw,H/ih):Math.min(fillW*W/cw,fillH*H/ch);
    return {s:s,photo:photo,ccx:cx+cw/2,ccy:cy+ch/2,W:W,H:H};
  }
  function set(img,g,s){
    /* first fit lands without the hover transition, or every card zooms as the page loads */
    var first=!img.hasAttribute('data-mmfit');
    if(first){img.style.transition='none';img.setAttribute('data-mmfit','1');}
    img.style.setProperty('--mmfit-s',s.toFixed(4));
    if(g.photo){
      img.style.setProperty('--mmfit-ox','50%');img.style.setProperty('--mmfit-oy','50%');
      img.style.setProperty('--mmfit-tx','0px');img.style.setProperty('--mmfit-ty','0px');
    }else{
      img.style.setProperty('--mmfit-ox',(g.ccx/g.W*100).toFixed(3)+'%');
      img.style.setProperty('--mmfit-oy',(g.ccy/g.H*100).toFixed(3)+'%');
      img.style.setProperty('--mmfit-tx',(g.W/2-g.ccx).toFixed(2)+'px');
      img.style.setProperty('--mmfit-ty',(g.H/2-g.ccy).toFixed(2)+'px');
    }
    if(first){void img.offsetWidth;requestAnimationFrame(function(){img.style.transition='';});}
  }
  function frameOf(img){var f=img.parentElement;return {W:f.clientWidth||1,H:f.clientHeight||1};}
  /* fit one image on its own */
  function fitOne(img,url,fill){
    return measure(url||img.currentSrc||img.src).then(function(box){
      if(!box)return;var f=frameOf(img);var g=geom(box,f.W,f.H,fill.w,fill.h);set(img,g,g.s);
    });
  }
  /* fit a set of images that must stay comparable: each centred on its own subject, all at the
     smallest scale any of them needs. imgs: [{img,url}] */
  function fitGroup(items,fill){
    return Promise.all(items.map(function(it){return measure(it.url||it.img.currentSrc||it.img.src);})).then(function(boxes){
      var gs=items.map(function(it,i){if(!boxes[i])return null;var f=frameOf(it.img);return geom(boxes[i],f.W,f.H,fill.w,fill.h);});
      var s=Infinity;gs.forEach(function(g){if(g&&!g.photo&&g.s<s)s=g.s;});
      gs.forEach(function(g,i){if(!g)return;set(items[i].img,g,g.photo?g.s:(isFinite(s)?s:g.s));});
    });
  }
  window.mmFit={measure:measure,fitOne:fitOne,fitGroup:fitGroup};

  /* THE GRID CARDS. The front picture is fitted alone. The size renders (the chips' data-img,
     swapped in on desktop hover by the section script) share the family's smallest scale; when the
     src changes the matching variables are re-applied. */
  var FILL={w:0.9,h:0.86};
  function cards(){
    var root=document.getElementById('mmpi');if(!root)return;
    root.querySelectorAll('.mmpi__card').forEach(function(card){
      var img=card.querySelector('.mmpi__img');if(!img)return;
      if(card._mmfit){card._mmfit();return;}
      var front=img.getAttribute('src');
      var urls=[].slice.call(card.querySelectorAll('.mmpi__sz')).map(function(a){return a.getAttribute('data-img');}).filter(Boolean);
      var f=frameOf(img);
      function apply(){
        var cur=img.getAttribute('src');
        if(cur===front||!urls.length){fitOne(img,front,FILL);return;}
        Promise.all(urls.map(measure)).then(function(boxes){
          var s=Infinity,mine=null;
          boxes.forEach(function(b,i){if(!b)return;var g=geom(b,f.W,f.H,FILL.w,FILL.h);if(!g.photo&&g.s<s)s=g.s;if(urls[i]===cur)mine=g;});
          if(mine)set(img,mine,mine.photo?mine.s:(isFinite(s)?s:mine.s));
        });
      }
      card._mmfit=function(){f=frameOf(img);apply();};
      /* Not gated on the <img> loading: the measurement fetches its own copy and the frame's
         size comes from layout, so a lazy picture still gets fitted before it appears. */
      apply();
      new MutationObserver(card._mmfit).observe(img,{attributes:true,attributeFilter:['src']});
    });
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',cards);else cards();
  var t;window.addEventListener('resize',function(){clearTimeout(t);t=setTimeout(cards,250);});
})();
